// "Sincronizar" de la lista de Tienda online, sin pasar por la lista de cambios de la tienda (esa pantalla no
// se muestra: ADR-2026-10-30-la-lista-de-cambios-no-se-muestra). Hace lo mismo que el botón de la ficha:
// elige lo modificado, revisa que esté completo, publica en Tiendanube y espera, deja cada ficha como
// quedó la tienda y, aparte, sincroniza en Mercado Libre lo que la tiene tildada.

import { fireTnExport, getTnStatus, leerPublicadosEnTn } from "../../lib/tiendaNube";
import { cambiosContraLaTienda, type ProductoEnLaTienda } from "./cambiosContraLaTienda";
import { limiteDeEsperaMs } from "./esperaDePublicacion";
import {
  fetchCategorias,
  fetchLanzamiento,
  firmarFull,
  problemasParaPublicar,
  revisarPublicacion,
  type ProductoDeLanzamiento,
} from "./lanzamientosApi";
import { publicarLanzamiento, YA_PUBLICANDO } from "./publicarLanzamiento";
import { sincronizarEnMercadoLibre, type ResultadoEnMl } from "./sincronizarEnMl";
import { enviadaDe, reiniciarConLaTienda } from "./traerDeLaTienda";
import { guardarSincronizarEn } from "./lanzamientosApi";

export interface ResultadoDeSincronizar {
  texto: string;
  error: boolean;
  /** Si a un producto le falta algo: su ficha, para abrirla. */
  abrir?: string;
}

const esperar = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Modificado y sin mandar: lo mismo que cuenta el botón (ver `modificados` en LanzamientoDetalle). */
function estaModificado(p: ProductoDeLanzamiento, publicado: ProductoEnLaTienda | undefined, arbol: Parameters<typeof cambiosContraLaTienda>[2]) {
  if (p.tnProductId == null) return p.estado !== "publicado";
  if (p.imagenes.length > 0 || (p.fotosABorrar?.length ?? 0) > 0) return true;
  return !!publicado && cambiosContraLaTienda(p, publicado, arbol).cambios.length > 0;
}

export async function sincronizarLaTienda(p: {
  lanzamientoId: string;
  companyId: string;
  brandId: string | null;
  ml: { disponible: boolean; conectada: boolean };
  alAvanzar?: (subidas: number, total: number) => void;
  /** Sólo estas filas (la edición masiva). Sin esto, todo lo modificado. */
  soloFilas?: readonly string[];
  /** false = en Mercado Libre sólo se edita lo que ya está (la edición masiva no crea publicaciones). */
  crearEnMl?: boolean;
}): Promise<ResultadoDeSincronizar> {
  const detalle = await fetchLanzamiento(p.lanzamientoId, p.companyId);
  if (!detalle) return { texto: "No pudimos leer los cambios de tu tienda. Probá de nuevo.", error: true };
  const arbol = await fetchCategorias(p.companyId, p.brandId);
  const ids = [...new Set(detalle.productos_.flatMap((x) => (x.tnProductId != null ? [x.tnProductId] : [])))];
  const publicados = ids.length > 0 ? await leerPublicadosEnTn(p.brandId, ids) : new Map<number, ProductoEnLaTienda>();
  if (ids.length > 0 && !publicados) {
    return { texto: "No se pudo leer cómo están tus productos en Tiendanube. No se cambió nada; probá en un rato.", error: true };
  }
  const deLaTienda = (x: ProductoDeLanzamiento) => (x.tnProductId != null ? publicados!.get(x.tnProductId) : undefined);
  const modificados = detalle.productos_.filter(
    (x) => x.productoId && (!p.soloFilas || p.soloFilas.includes(x.id)) && estaModificado(x, deLaTienda(x), arbol),
  );
  if (modificados.length === 0) return { texto: "No hay cambios para sincronizar.", error: false };

  const vaATn = (x: ProductoDeLanzamiento) => x.sincronizarEn == null || x.sincronizarEn.includes("tiendanube");
  const vaAMl = (x: ProductoDeLanzamiento) => p.ml.disponible && (x.sincronizarEn == null || x.sincronizarEn.includes("mercadolibre"));
  const aTn = modificados.filter(vaATn);

  const problemas = problemasParaPublicar(aTn, ["tiendanube"]);
  if (problemas.length > 0) {
    const pr = problemas[0]!;
    const fila = aTn.find((x) => x.productoId === pr.productoId);
    return {
      texto:
        problemas.length === 1
          ? `${pr.nombre}: ${pr.motivo}.`
          : `${problemas.length} productos sin completar. El primero, ${pr.nombre}: ${pr.motivo}.`,
      error: true,
      ...(fila ? { abrir: `/lanzamientos/${detalle.id}?producto=${fila.id}` } : {}),
    };
  }

  // Lo que se manda de cada ficha: después se limpia sólo eso (lo que se toque mientras tanto queda pendiente).
  const enviadas = new Map(modificados.map((x) => [x.id, enviadaDe(x)]));

  // Mercado Libre, aparte y a la vez: con lo que cambió ANTES de que Tiendanube lo publique.
  const enMl: Promise<ResultadoEnMl | null> = (async () => {
    const paraMl = modificados.filter(vaAMl).map((x) => {
      const publicado = deLaTienda(x);
      return {
        producto: x,
        cambios: publicado ? cambiosContraLaTienda(x, publicado, arbol).cambios : [],
        crearSiFalta: p.crearEnMl !== false && x.sincronizarEn?.includes("mercadolibre") === true,
      };
    });
    if (paraMl.length === 0) return null;
    return sincronizarEnMercadoLibre({ detalle, productos: paraMl, brandId: p.brandId, cuentaConectada: p.ml.conectada });
  })();

  let textoTn = "";
  let errorTn = false;
  /** Los que quedaron publicados en Tiendanube. null = no se sabe (la espera venció o no se mandó). */
  let enTnListos: Set<string> | null = null;
  if (aTn.length > 0) {
    const r = await publicarLanzamiento(
      { ...detalle, productos_: aTn },
      {
        estadoDeLaTienda: () => getTnStatus(p.brandId),
        firmar: (fileName) => firmarFull(p.companyId, fileName),
        categorias: async () => arbol,
        publicados: (tnIds) => leerPublicadosEnTn(p.brandId, tnIds),
        disparar: (productos, lanzamientoId) => fireTnExport({ companyId: p.companyId, brandId: p.brandId, productos, lanzamientoId }),
      },
    );
    if (r.estado === "lanzado") {
      // Se espera a que el worker termine, como el botón de la ficha.
      const desde = Date.now();
      const limite = limiteDeEsperaMs({ productos: r.productos, fotos: r.imagenes });
      let final: Awaited<ReturnType<typeof revisarPublicacion>> | null = null;
      while (Date.now() - desde < limite) {
        await esperar(3000);
        const estado = await revisarPublicacion(detalle.id, p.companyId);
        p.alAvanzar?.(estado.subidas, estado.total || r.productos);
        if (estado.terminado) {
          final = estado;
          break;
        }
      }
      if (!final) {
        textoTn = "Sigue sincronizándose en Tiendanube: los productos se van actualizando solos.";
      } else {
        const despues = await fetchLanzamiento(detalle.id, p.companyId).catch(() => null);
        const mandados = new Set(aTn.map((x) => x.id));
        enTnListos = new Set((despues?.productos_ ?? []).filter((x) => mandados.has(x.id) && x.estado === "publicado").map((x) => x.id));
        errorTn = final.conErrores;
        textoTn = final.conErrores
          ? `Terminó, pero algunos productos no se pudieron sincronizar.${final.motivo ? ` ${final.motivo.replace(/\.$/, "")}.` : ""}`
          : aTn.length === 1
            ? "Listo, se sincronizó el producto en Tiendanube."
            : `Listo, se sincronizaron ${aTn.length} productos en Tiendanube.`;
      }
    } else if (r.estado === "sin-tienda") {
      textoTn = "Tiendanube no está conectada.";
      errorTn = true;
    } else if (r.estado === "sin-cambios") {
      textoTn = "En Tiendanube ya estaba todo así.";
    } else if (r.estado === "rechazado" && r.motivo === YA_PUBLICANDO) {
      textoTn = "Ya se está sincronizando en Tiendanube. Esperá a que termine.";
    } else {
      textoTn = "No se pudo sincronizar en Tiendanube. No se cambió nada; probá de nuevo.";
      errorTn = true;
    }
  }

  const ml = await enMl;

  // La ficha vuelve a ser la tienda sólo si quedó bien en TODAS las tiendas a las que iba (review Greptile de
  // #232): si Mercado Libre falló, lo pendiente hace falta para reintentar.
  const tnBien = (x: ProductoDeLanzamiento) => !vaATn(x) || (enTnListos?.has(x.id) ?? false);
  const mlBien = (x: ProductoDeLanzamiento) => !vaAMl(x) || (ml?.listas.has(x.id) ?? false);
  const listos = modificados.filter((x) => tnBien(x) && mlBien(x));
  // En Tiendanube quedó y en Mercado Libre no: el próximo Sincronizar va sólo a Mercado Libre, así Tiendanube
  // no recibe las fotos dos veces.
  for (const x of modificados.filter((x) => vaATn(x) && tnBien(x) && !mlBien(x))) {
    await guardarSincronizarEn(x.id, ["mercadolibre"]).catch(() => {});
  }
  if (listos.length > 0) {
    const despues = await fetchLanzamiento(detalle.id, p.companyId).catch(() => null);
    const ids = new Set(listos.map((x) => x.id));
    await reiniciarConLaTienda(
      (despues?.productos_ ?? []).filter((x) => ids.has(x.id)),
      p.brandId,
      arbol,
      p.companyId,
      enviadas,
      () => fetchLanzamiento(detalle.id, p.companyId).then((d) => d?.productos_ ?? null),
    );
  }
  const textoMl = ml
    ? [
        ml.cambiadas > 0 || ml.creados > 0
          ? `Mercado Libre: ${[
              ml.cambiadas > 0 ? `${ml.cambiadas === 1 ? "1 publicación actualizada" : `${ml.cambiadas} publicaciones actualizadas`}` : "",
              ml.creados > 0 ? `${ml.creados === 1 ? "1 producto mandado a crear" : `${ml.creados} productos mandados a crear`}` : "",
            ]
              .filter(Boolean)
              .join(" y ")}.`
          : "",
        ml.noSeAplican.length > 0 ? `No se aplica en Mercado Libre: ${ml.noSeAplican.join(", ")}.` : "",
        ...ml.fallas.map((f) => `${f}.`),
        ...ml.notas.map((n) => `${n}.`),
      ]
        .filter(Boolean)
        .join(" ")
    : "";
  return {
    texto: [textoTn, textoMl].filter(Boolean).join(" ") || "Listo.",
    error: errorTn || (ml?.fallas.length ?? 0) > 0,
  };
}
